const TURNSTILE_SRC =
  "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";

/**
 * Cached across calls, including the rejected case. Ad blockers commonly block
 * challenges.cloudflare.com, and we do not want a remount to retry the injection
 * on every navigation.
 */
let loadPromise: Promise<TurnstileApi> | null = null;

/**
 * The timeout is how long an ad-blocked user stares at the map placeholder
 * before we give up and show the map anyway, so keep it short. The Cloudflare
 * script normally loads well under a second.
 */
export function loadTurnstile(timeoutMs = 4000): Promise<TurnstileApi> {
  if (loadPromise) return loadPromise;

  loadPromise = new Promise<TurnstileApi>((resolve, reject) => {
    if (typeof window === "undefined" || typeof document === "undefined") {
      reject(new Error("turnstile-ssr"));
      return;
    }
    if (window.turnstile) {
      resolve(window.turnstile);
      return;
    }

    const timer = window.setTimeout(
      () => reject(new Error("turnstile-timeout")),
      timeoutMs
    );

    const script = document.createElement("script");
    script.src = TURNSTILE_SRC;
    script.async = true;
    script.defer = true;
    script.onload = () => {
      window.clearTimeout(timer);
      if (window.turnstile) resolve(window.turnstile);
      else reject(new Error("turnstile-missing"));
    };
    script.onerror = () => {
      window.clearTimeout(timer);
      reject(new Error("turnstile-blocked"));
    };
    document.head.appendChild(script);
  });

  return loadPromise;
}

/** Test-only: clears the module-level cache between cases. */
export function resetTurnstileLoaderForTests(): void {
  loadPromise = null;
}
